import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Popover,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiPost, apiGet, apiPatch } from '../utils/apiUtils';
import AdminDashboard from './AdminDashboard';
import { useNavigate } from 'react-router-dom';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',    // Primary blue
  orange: '#ffaa01',  // Warning/accent orange
  pink: '#df3e99'     // Error/critical pink
};

const AdminPanel = () => {
  const { user, impersonateUser, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirect to home if impersonating (admins shouldn't see admin panel when impersonating)
  useEffect(() => {
    if (isImpersonating) {
      navigate('/');
    }
  }, [isImpersonating, navigate]);

  // Sorting state
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');

  // Tier filter state - all tiers selected by default
  const [selectedTiers, setSelectedTiers] = useState([
    'free', 'trial_user', 'basic', 'pro', 'organization', 'agency_client', 'internal'
  ]);

  // Create user form
  const [createUserForm, setCreateUserForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
    tier: 'trial_user'
  });

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    user: null
  });
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [changeTierDialog, setChangeTierDialog] = useState({
    open: false,
    user: null,
    newTier: ''
  });
  const [impersonationDialog, setImpersonationDialog] = useState({
    open: false,
    user: null
  });

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // All tiers for filtering
  const tiers = [
    { value: 'free', label: 'Free (100 credits)' },
    { value: 'trial_user', label: 'Trial User (5,000 credits → Free after 1 month)' },
    { value: 'basic', label: 'Basic (2,000 credits) - $9/mo' },
    { value: 'pro', label: 'Pro (10,000 credits) - $39/mo' },
    { value: 'organization', label: 'Organization (30,000 credits) - $99/mo' },
    { value: 'agency_client', label: 'Agency Client (10,000 credits)' },
    { value: 'internal', label: 'Internal (Unlimited)' }
  ];

  // Non-Stripe tiers only (for admin user creation and tier changes)
  const nonStripeTiers = [
    { value: 'free', label: 'Free (100 credits/month)' },
    { value: 'trial_user', label: 'Trial User (5,000 credits → Free after 1 month)' },
    { value: 'agency_client', label: 'Agency Client (10,000 credits/month)' },
    { value: 'internal', label: 'Internal (Unlimited)' }
  ];

  // Helper function to check if user is Stripe-managed
  const isStripeManaged = (user) => {
    return user.stripe_subscription_id != null ||
           ['basic', 'pro', 'organization'].includes(user.tier);
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const rawResponse = await apiGet('/api/usage/admin/users');
      
      if (!rawResponse.ok) {
        throw new Error(`HTTP error! status: ${rawResponse.status}`);
      }
      
      const response = await rawResponse.json();
      
      if (response.success) {
        setUsers(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(`Failed to fetch users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted and filtered users
  const getSortedUsers = () => {
    if (!users || users.length === 0) return [];

    // Filter by selected tiers
    const filteredUsers = users.filter(user =>
      selectedTiers.includes(user.tier || 'free')
    );

    // Sort filtered users
    return [...filteredUsers].sort((a, b) => {
      let aVal, bVal;

      // Special handling for calculated total_jobs field
      if (sortField === 'total_jobs') {
        aVal = (a.total_filter_jobs || 0) + (a.total_research_jobs || 0);
        bVal = (b.total_filter_jobs || 0) + (b.total_research_jobs || 0);
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Convert to lowercase for string comparison
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  // Toggle tier selection
  const handleTierToggle = (tier) => {
    setSelectedTiers(prev => {
      if (prev.includes(tier)) {
        // Remove tier if already selected
        return prev.filter(t => t !== tier);
      } else {
        // Add tier if not selected
        return [...prev, tier];
      }
    });
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;

    try {
      setError('');
      setSuccess('');
      setDeleteDialog({ open: false, user: null });

      const { apiDelete } = await import('../utils/apiUtils');
      const rawResponse = await apiDelete(`/api/usage/admin/delete-user/${deleteDialog.user.id}`);

      const response = await rawResponse.json();

      if (response.success) {
        setSuccess(`Successfully deleted user ${deleteDialog.user.email}`);
        fetchUsers(); // Refresh users list
      } else {
        throw new Error(response.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(`Failed to delete user: ${error.message}`);
    }
  };

  // Change user tier
  const handleChangeTier = async () => {
    if (!changeTierDialog.user || !changeTierDialog.newTier) return;

    try {
      setError('');
      setSuccess('');
      setChangeTierDialog({ open: false, user: null, newTier: '' });

      const rawResponse = await apiPost('/api/usage/admin/change-tier', {
        userId: changeTierDialog.user.id,
        tier: changeTierDialog.newTier
      });

      const response = await rawResponse.json();

      if (response.success) {
        setSuccess(`Successfully changed tier for ${changeTierDialog.user.email} to ${changeTierDialog.newTier}`);
        fetchUsers(); // Refresh users list
      } else {
        throw new Error(response.error || 'Failed to change tier');
      }
    } catch (error) {
      console.error('Error changing tier:', error);
      setError(`Failed to change tier: ${error.message}`);
    }
  };

  // Handle impersonation
  const handleImpersonate = async () => {
    if (!impersonationDialog.user) return;

    try {
      setError('');
      setImpersonationDialog({ open: false, user: null });

      // Call impersonateUser from AuthContext
      await impersonateUser(impersonationDialog.user.id);

      // Note: Page will reload after successful impersonation
    } catch (error) {
      console.error('Error impersonating user:', error);
      setError(`Failed to impersonate user: ${error.message}`);
    }
  };

  // Toggle web search feature for user
  const handleWebSearchToggle = async (user) => {
    try {
      setError('');
      setSuccess('');

      const newValue = !user.enable_web_search_feature;

      const rawResponse = await apiPatch(`/api/usage/admin/users/${user.id}/feature-flags`, {
        enable_web_search_feature: newValue
      });

      const response = await rawResponse.json();

      if (response.success) {
        setSuccess(`Successfully ${newValue ? 'enabled' : 'disabled'} web search for ${user.email}`);
        fetchUsers(); // Refresh users list
      } else {
        throw new Error(response.error || 'Failed to update feature flag');
      }
    } catch (error) {
      console.error('Error toggling web search:', error);
      setError(`Failed to update web search feature: ${error.message}`);
    }
  };

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      // Validate all required fields
      if (!createUserForm.firstName || !createUserForm.lastName || !createUserForm.companyName ||
          !createUserForm.email || !createUserForm.password || !createUserForm.confirmPassword) {
        setError('All fields are required');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(createUserForm.email)) {
        setError('Please enter a valid email address');
        return;
      }

      // Validate password length
      if (createUserForm.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      // Validate password match
      if (createUserForm.password !== createUserForm.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      const rawResponse = await apiPost('/api/usage/admin/create-user', {
        firstName: createUserForm.firstName,
        lastName: createUserForm.lastName,
        companyName: createUserForm.companyName,
        email: createUserForm.email,
        password: createUserForm.password,
        tier: createUserForm.tier
      });

      const response = await rawResponse.json();

      if (response.success) {
        setSuccess(`Successfully created user ${createUserForm.firstName} ${createUserForm.lastName} (${createUserForm.email})`);
        setCreateUserForm({
          firstName: '',
          lastName: '',
          companyName: '',
          email: '',
          password: '',
          confirmPassword: '',
          tier: 'trial_user'
        });
        setCreateUserDialog(false);
        fetchUsers(); // Refresh users list
      } else {
        throw new Error(response.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(`Failed to create user: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'internal': return 'success';
      case 'agency_user': return 'info';
      case 'beta_tester': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 100%)',
      color: 'white',
      p: 3
    }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/account')}
          sx={{
            color: wyspurColors.blue,
            '&:hover': {
              backgroundColor: `${wyspurColors.blue}15`
            }
          }}
        >
          Back to Account
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ color: wyspurColors.blue, fontWeight: 600 }}>
        Admin Panel
      </Typography>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: wyspurColors.blue
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: wyspurColors.blue
            }
          }}
        >
          <Tab 
            icon={<DashboardIcon />} 
            label="Analytics Dashboard" 
            iconPosition="start"
          />
          <Tab 
            icon={<PeopleIcon />} 
            label="User Management" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && <AdminDashboard />}
      
      {currentTab === 1 && (
        <Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            backgroundColor: `${wyspurColors.pink}15`,
            border: `1px solid ${wyspurColors.pink}40`,
            color: wyspurColors.pink,
            '& .MuiAlert-icon': { color: wyspurColors.pink }
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            backgroundColor: `${wyspurColors.blue}15`,
            border: `1px solid ${wyspurColors.blue}40`,
            color: wyspurColors.blue,
            '& .MuiAlert-icon': { color: wyspurColors.blue }
          }} 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: wyspurColors.blue }}>
          All Users
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={fetchUsers}
            sx={{
              borderColor: wyspurColors.blue,
              color: wyspurColors.blue,
              '&:hover': {
                borderColor: `${wyspurColors.blue}dd`,
                backgroundColor: `${wyspurColors.blue}15`
              }
            }}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Users'}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateUserDialog(true)}
            sx={{
              backgroundColor: wyspurColors.orange,
              '&:hover': {
                backgroundColor: `${wyspurColors.orange}dd`
              }
            }}
          >
            Create New User
          </Button>

          {/* Tier Filter Button - moved next to Create User */}
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              borderColor: wyspurColors.blue,
              color: wyspurColors.blue,
              '&:hover': {
                borderColor: `${wyspurColors.blue}dd`,
                backgroundColor: `${wyspurColors.blue}15`
              }
            }}
          >
            Filter Tiers ({selectedTiers.length})
          </Button>

          {/* Tier Filter Popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(15, 20, 25, 0.98)',
                border: `1px solid ${wyspurColors.blue}40`,
                color: 'white',
                p: 2,
                minWidth: 250
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, color: wyspurColors.blue, fontWeight: 600 }}>
              Filter by Tier
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {tiers.map(tier => (
                <FormControlLabel
                  key={tier.value}
                  control={
                    <Checkbox
                      checked={selectedTiers.includes(tier.value)}
                      onChange={() => handleTierToggle(tier.value)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: wyspurColors.blue
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {tier.value}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Popover>
        </Box>

        {users.length > 0 ? (
          <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr 1fr'
              },
              gap: 2,
              p: 2,
              mb: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(15, 113, 251, 0.08)',
              border: '1px solid rgba(15, 113, 251, 0.2)',
              color: 'white'
            }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('email')}
              >
                Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('tier')}
              >
                Tier {sortField === 'tier' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('credits_available')}
              >
                Available (Monthly) {sortField === 'credits_available' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('credits_used_this_month')}
              >
                Used (Monthly) {sortField === 'credits_used_this_month' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('total_jobs')}
              >
                Total Jobs {sortField === 'total_jobs' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('current_period_end')}
              >
                Reset Date {sortField === 'current_period_end' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                  '&:hover': { color: wyspurColors.blue }
                }}
                onClick={() => handleSort('last_sign_in_at')}
              >
                Last Login {sortField === 'last_sign_in_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                Web Search
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                Actions
              </Typography>
            </Box>

            {/* Data Rows */}
            {getSortedUsers().map((user, index) => (
              <Paper key={user.id} sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr 1fr'
                },
                gap: 2,
                p: 2,
                mb: 1,
                backgroundColor: index % 2 === 0 ? 'rgba(15, 113, 251, 0.03)' : 'rgba(15, 113, 251, 0.01)',
                border: '1px solid rgba(15, 113, 251, 0.15)',
                borderRadius: 1,
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(15, 113, 251, 0.08)'
                }
              }}>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Email:</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'white' }}>{user.email}</Typography>
                
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Tier:</Typography>
                </Box>
                <Box sx={{ textAlign: { md: 'center' } }}>
                  <Chip 
                    label={user.tier || 'beta_tester'} 
                    size="small"
                    sx={{
                      backgroundColor: getTierColor(user.tier) === 'success' ? `${wyspurColors.blue}20` : 
                                     getTierColor(user.tier) === 'info' ? `${wyspurColors.orange}20` : 
                                     'rgba(255, 255, 255, 0.1)',
                      color: getTierColor(user.tier) === 'success' ? wyspurColors.blue : 
                             getTierColor(user.tier) === 'info' ? wyspurColors.orange : 'white',
                      border: `1px solid ${getTierColor(user.tier) === 'success' ? wyspurColors.blue : 
                                           getTierColor(user.tier) === 'info' ? wyspurColors.orange : 'rgba(255, 255, 255, 0.2)'}`
                    }}
                  />
                </Box>
                
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Credits Available:</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: { md: 'center' }, color: 'white' }}>
                  {user.credits_available?.toLocaleString() || 0}
                </Typography>
                
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Credits Used:</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: { md: 'center' }, color: 'white' }}>
                  {user.credits_used_this_month?.toLocaleString() || 0}
                </Typography>

                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total Jobs:</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: { md: 'center' }, color: 'white' }}>
                  {(user.total_filter_jobs || 0) + (user.total_research_jobs || 0)}
                </Typography>

                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Reset Date:</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: { md: 'center' }, color: 'white' }}>
                  {formatDate(user.current_period_end)}
                </Typography>

                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Last Login:</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: { md: 'center' }, color: 'white' }}>
                  {formatDate(user.last_sign_in_at)}
                </Typography>

                {/* Web Search Toggle */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Web Search:</Typography>
                </Box>
                <Box sx={{ textAlign: { md: 'center' }, display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' }, alignItems: 'center' }}>
                  <Tooltip title={user.enable_web_search_feature ? "Disable enhanced web search" : "Enable enhanced web search"} arrow>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={user.enable_web_search_feature || false}
                          onChange={() => handleWebSearchToggle(user)}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.3)',
                            '&.Mui-checked': {
                              color: wyspurColors.blue
                            }
                          }}
                          size="small"
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Tooltip>
                </Box>

                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Actions:</Typography>
                </Box>
                <Box sx={{ textAlign: { md: 'center' }, display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'center' } }}>
                  {/* Login As Button */}
                  <Tooltip title="Login as this user" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setImpersonationDialog({ open: true, user })}
                      sx={{
                        color: wyspurColors.blue,
                        '&:hover': {
                          backgroundColor: `${wyspurColors.blue}20`,
                          color: wyspurColors.blue
                        }
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Change Tier Button */}
                  <Tooltip
                    title={isStripeManaged(user)
                      ? "Stripe-managed users must change their subscription through the billing portal"
                      : "Change user tier"
                    }
                    arrow
                  >
                    <span> {/* Wrapper needed for Tooltip on disabled button */}
                      <IconButton
                        size="small"
                        disabled={isStripeManaged(user)}
                        onClick={() => setChangeTierDialog({ open: true, user, newTier: user.tier })}
                        sx={{
                          color: isStripeManaged(user) ? 'rgba(255, 255, 255, 0.3)' : wyspurColors.orange,
                          '&:hover': {
                            backgroundColor: isStripeManaged(user) ? 'transparent' : `${wyspurColors.orange}20`,
                            color: isStripeManaged(user) ? 'rgba(255, 255, 255, 0.3)' : wyspurColors.orange
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Delete Button */}
                  <Tooltip
                    title={isStripeManaged(user)
                      ? "Cannot delete users with active Stripe subscriptions. Please cancel their subscription first."
                      : `Delete ${user.email}`
                    }
                    arrow
                  >
                    <span> {/* Wrapper needed for Tooltip on disabled button */}
                      <IconButton
                        size="small"
                        disabled={isStripeManaged(user)}
                        onClick={() => setDeleteDialog({ open: true, user })}
                        sx={{
                          color: isStripeManaged(user) ? 'rgba(255, 255, 255, 0.3)' : wyspurColors.pink,
                          '&:hover': {
                            backgroundColor: isStripeManaged(user) ? 'transparent' : `${wyspurColors.pink}20`,
                            color: isStripeManaged(user) ? 'rgba(255, 255, 255, 0.3)' : wyspurColors.pink
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Alert 
            severity="info"
            sx={{
              backgroundColor: `${wyspurColors.blue}15`,
              border: `1px solid ${wyspurColors.blue}40`,
              color: wyspurColors.blue,
              '& .MuiAlert-icon': { color: wyspurColors.blue }
            }}
          >
            No users found. {loading && 'Loading...'}
          </Alert>
        )}
      </Box>

      {/* Impersonation Confirmation Dialog */}
      <Dialog
        open={impersonationDialog.open}
        onClose={() => setImpersonationDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            border: `1px solid ${wyspurColors.blue}40`,
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ color: wyspurColors.blue }}>
          Login as User
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            You are about to impersonate <strong>{impersonationDialog.user?.email}</strong>.
            <br/><br/>
            You will be able to:
            <br/>• View their account exactly as they see it
            <br/>• Access their data and research jobs
            <br/>• Perform actions on their behalf
            <br/><br/>
            An orange banner will appear at the top to remind you that you're in impersonation mode.
            You can exit anytime by clicking "Exit Impersonation" in the banner.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImpersonationDialog({ open: false, user: null })}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImpersonate}
            variant="contained"
            sx={{
              backgroundColor: wyspurColors.blue,
              '&:hover': {
                backgroundColor: `${wyspurColors.blue}dd`
              }
            }}
          >
            Login as User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            border: `1px solid ${wyspurColors.pink}40`,
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ color: wyspurColors.pink }}>
          Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Are you sure you want to delete the user <strong>{deleteDialog.user?.email}</strong>?
            <br/><br/>
            This will permanently delete:
            <br/>• User account and profile
            <br/>• All credits and usage logs  
            <br/>• All research jobs and saved filters
            <br/><br/>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, user: null })}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser}
            variant="contained"
            sx={{ 
              backgroundColor: wyspurColors.pink,
              '&:hover': {
                backgroundColor: `${wyspurColors.pink}dd`
              }
            }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Tier Dialog */}
      <Dialog
        open={changeTierDialog.open}
        onClose={() => setChangeTierDialog({ open: false, user: null, newTier: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            border: `1px solid ${wyspurColors.orange}40`,
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ color: wyspurColors.orange }}>
          Change User Tier
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
            Change tier for <strong>{changeTierDialog.user?.email}</strong>
            <br/><br/>
            Current tier: <strong>{changeTierDialog.user?.tier}</strong>
            <br/><br/>
            <em>Note: Only non-Stripe tiers can be changed here. Stripe-managed users must update their subscription through the billing portal.</em>
          </DialogContentText>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>New Tier</InputLabel>
            <Select
              value={changeTierDialog.newTier}
              onChange={(e) => setChangeTierDialog(prev => ({ ...prev, newTier: e.target.value }))}
              label="New Tier"
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: `${wyspurColors.orange}40`
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${wyspurColors.orange}60`
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: wyspurColors.orange
                },
                '.MuiSvgIcon-root': {
                  color: wyspurColors.orange
                }
              }}
            >
              {nonStripeTiers.map(tier => (
                <MenuItem key={tier.value} value={tier.value}>
                  {tier.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setChangeTierDialog({ open: false, user: null, newTier: '' })}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleChangeTier}
            variant="contained"
            disabled={!changeTierDialog.newTier || changeTierDialog.newTier === changeTierDialog.user?.tier}
            sx={{
              backgroundColor: wyspurColors.orange,
              '&:hover': {
                backgroundColor: `${wyspurColors.orange}dd`
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 170, 1, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Change Tier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        open={createUserDialog}
        onClose={() => setCreateUserDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 20, 25, 0.95)',
            border: `1px solid ${wyspurColors.orange}40`,
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ color: wyspurColors.orange }}>
          Create New User
        </DialogTitle>
        <DialogContent>
          <Box component="form" id="create-user-form" onSubmit={handleCreateUser} sx={{ mt: 2 }}>
            {/* First Name */}
            <TextField
              fullWidth
              label="First Name"
              value={createUserForm.firstName}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, firstName: e.target.value }))}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Last Name */}
            <TextField
              fullWidth
              label="Last Name"
              value={createUserForm.lastName}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, lastName: e.target.value }))}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Company Name */}
            <TextField
              fullWidth
              label="Company Name"
              value={createUserForm.companyName}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, companyName: e.target.value }))}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={createUserForm.email}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Password */}
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={createUserForm.password}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
              margin="normal"
              required
              helperText="Minimum 6 characters"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Confirm Password */}
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={createUserForm.confirmPassword}
              onChange={(e) => setCreateUserForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              margin="normal"
              required
              helperText="Must match password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: wyspurColors.orange },
                  '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': { color: wyspurColors.orange },
                },
                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' },
                '& .MuiOutlinedInput-input': { color: 'white !important' },
                '& input': { color: 'white !important' },
              }}
            />

            {/* Tier Selection */}
            <FormControl fullWidth margin="normal" required sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: wyspurColors.orange },
                '&.Mui-focused fieldset': { borderColor: wyspurColors.orange },
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.8)',
                '&.Mui-focused': { color: wyspurColors.orange },
              },
            }}>
              <InputLabel>User Tier</InputLabel>
              <Select
                value={createUserForm.tier}
                label="User Tier"
                onChange={(e) => setCreateUserForm(prev => ({ ...prev, tier: e.target.value }))}
                sx={{
                  color: 'white !important',
                  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.8)' },
                  '& .MuiSelect-select': { color: 'white !important' },
                }}
              >
                {nonStripeTiers.map(tier => (
                  <MenuItem key={tier.value} value={tier.value}>
                    {tier.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCreateUserDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            form="create-user-form"
            variant="contained"
            sx={{ 
              backgroundColor: wyspurColors.orange,
              '&:hover': {
                backgroundColor: `${wyspurColors.orange}dd`
              }
            }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default AdminPanel;